import { useAuiState } from '@assistant-ui/react';
import {
	BookOpenIcon,
	BriefcaseIcon,
	ChevronRightIcon,
	ClipboardListIcon,
	DownloadIcon,
	ExternalLinkIcon,
	MapIcon,
	SendIcon,
	ShoppingCartIcon,
	UserIcon,
} from 'lucide-react';
import Link from 'next/link';
import type { FC } from 'react';
import { useMemo } from 'react';

type EntityType = 'customer' | 'download' | 'estimate' | 'floor-plan-summary' | 'help-article' | 'job' | 'purchase-order' | 'submittal';

interface ToolLink {
	href: string;
	label: string;
	type?: EntityType;
}

const typeConfig: Record<EntityType, { icon: FC<{ className?: string }>; subtitle: string }> = {
	'customer': { icon: UserIcon, subtitle: 'Customer' },
	'download': { icon: DownloadIcon, subtitle: 'Download' },
	'estimate': { icon: ClipboardListIcon, subtitle: 'Estimate' },
	'floor-plan-summary': { icon: MapIcon, subtitle: 'Floor plan summary' },
	'help-article': { icon: BookOpenIcon, subtitle: 'Help article' },
	'job': { icon: BriefcaseIcon, subtitle: 'Job' },
	'purchase-order': { icon: ShoppingCartIcon, subtitle: 'Purchase Order' },
	'submittal': { icon: SendIcon, subtitle: 'Submittal' },
};

export const AssistantMessageLinks: FC = () => {
	// @ts-ignore — moduleResolution:"node" prevents ScopeRegistry augmentation from applying
	const content = useAuiState((s) => s.message.content);

	const links = useMemo(() => {
		const result: ToolLink[] = [];
		const seen = new Set<string>();
		for (const part of content) {
			if (part.type !== 'tool-call') continue;
			const partLinks = (part.result as { links?: ToolLink[] } | undefined)?.links;
			if (!Array.isArray(partLinks)) continue;
			for (const link of partLinks) {
				if (!link.href || !link.label || seen.has(link.href)) continue;
				seen.add(link.href);
				result.push(link);
			}
		}
		return result;
	}, [content]);

	if (links.length === 0) return null;

	return (
		<div className="mt-3 grid grid-cols-2 gap-2">
			{links.map((link) => {
				const config = link.type ? typeConfig[link.type] : null;
				const Icon = config?.icon ?? ExternalLinkIcon;
				const cardClassName = 'group flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3 transition-colors hover:bg-muted/60';
				const cardContent = (
					<>
						<div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
							<Icon className="size-4" />
						</div>
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-medium text-primary">{link.label}</div>
							{config?.subtitle && <div className="text-xs text-muted-foreground">{config.subtitle}</div>}
						</div>
						<ChevronRightIcon className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
					</>
				);

				if (link.type === 'download' || link.type === 'help-article') {
					return (
						<a className={cardClassName} href={link.href} key={link.href} rel="noreferrer" target="_blank">
							{cardContent}
						</a>
					);
				}

				return (
					<Link className={cardClassName} href={link.href} key={link.href}>
						{cardContent}
					</Link>
				);
			})}
		</div>
	);
};
